import { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    IconButton,
    List,
    ListItemButton,
    ListItemSecondaryAction,
    ListItemText,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import PublicIcon from '@mui/icons-material/Public';
import { useDropzone } from 'react-dropzone';
import {
    Architect,
    configurePdfWorker,
    loadSavedProject,
    removeSavedProject,
    saveProject,
    type SavedProject,
} from 'architect-software';
import 'architect-software/style.css';
import './architect-overrides.css';
import { QuoteAttachmentPreviewDialog } from '@/components/QuoteAttachmentPreviewDialog';
import {
    calculatedPdfFilename,
    calculatedPngFilename,
    defaultAttachmentName,
    deleteQuoteAttachment,
    downloadQuoteAttachment,
    fetchQuoteAttachmentBlob,
    isLocalAttachmentId,
    isImageAttachment,
    isPdfAttachment,
    QuoteAttachment,
    uploadQuoteAttachment,
} from '@/utils/quote_attachment_utils';
import { useNotify } from 'react-admin';

const PDF_ACCEPT = {
    'application/pdf': ['.pdf'],
};

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_KEY as string | undefined;

type ArchitectEngineerTabProps = {
    files: QuoteAttachment[];
    onFilesChange: (files: QuoteAttachment[]) => void;
    /** Snapshot restored from quote estimation_data / ui_draft. */
    initialProject?: SavedProject | null;
    /** Persist snapshot to quote state (and localStorage via saveProject). */
    onChange?: (project: SavedProject) => void;
};

function isImageViewAttachment(attachment: QuoteAttachment): boolean {
    return (
        isImageAttachment(attachment.content_type) ||
        /\.(png|jpe?g)$/i.test(attachment.filename)
    );
}

function isViewListAttachment(attachment: QuoteAttachment): boolean {
    return isPdfAttachment(attachment) || isImageViewAttachment(attachment);
}

function matchesProjectFile(
    attachment: QuoteAttachment,
    project: SavedProject | null | undefined,
): boolean {
    if (!project?.fileName) return false;
    return (
        attachment.filename === project.fileName ||
        attachment.name === project.fileName
    );
}

/** Quote snapshot first; else localStorage (same key Architect's restore dialog uses). */
export function resolveSeedProject(
    attachment: QuoteAttachment,
    initialProject: SavedProject | null | undefined,
): SavedProject | null {
    if (matchesProjectFile(attachment, initialProject)) {
        return initialProject ?? null;
    }
    return loadSavedProject(attachment.filename);
}

async function fetchAttachmentAsFile(attachment: QuoteAttachment): Promise<File> {
    const blob = await fetchQuoteAttachmentBlob(attachment);
    return new File([blob], attachment.filename, {
        type: attachment.content_type || 'application/pdf',
    });
}

export function ArchitectEngineerTab({
    files,
    onFilesChange,
    initialProject = null,
    onChange,
}: ArchitectEngineerTabProps) {
    const notify = useNotify();
    const [uploading, setUploading] = useState(false);
    const [loadingId, setLoadingId] = useState<string | null>(null);
    const [error, setError] = useState('');
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [architectFile, setArchitectFile] = useState<File | null>(null);
    /** Snapshot applied once when opening a PDF — not updated on every onChange. */
    const [seedProject, setSeedProject] = useState<SavedProject | null>(null);
    /** Latest project from onChange — remount seed for fullscreen expand/collapse. */
    const [latestProject, setLatestProject] = useState<SavedProject | null>(null);
    /** Bump to remount Earth Architect as a fresh create-from-zero session. */
    const [earthSessionKey, setEarthSessionKey] = useState(0);
    const [previewImage, setPreviewImage] = useState<QuoteAttachment | null>(null);
    const [fullscreen, setFullscreen] = useState(false);
    const lastProjectFileNameRef = useRef<string | null>(null);

    useEffect(() => {
        import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url')
            .then(mod => configurePdfWorker(mod.default))
            .catch(() => {
                // ponytail: library configures worker from its bundle when this fails
            });
    }, []);

    const viewFiles = files.filter(isViewListAttachment);

    const openAttachment = useCallback(async (
        attachment: QuoteAttachment,
        preferredSeed: SavedProject | null = null,
    ) => {
        if (!isPdfAttachment(attachment)) return;
        setError('');
        setLoadingId(attachment.id);
        try {
            const file = await fetchAttachmentAsFile(attachment);
            const seed = resolveSeedProject(attachment, preferredSeed ?? initialProject);
            setSelectedId(attachment.id);
            setSeedProject(seed);
            setLatestProject(seed);
            setArchitectFile(file);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to open PDF');
        } finally {
            setLoadingId(null);
        }
    }, [initialProject]);

    const startFromZero = useCallback(() => {
        const key = lastProjectFileNameRef.current;
        if (key) {
            try { removeSavedProject(key); } catch { /* ignore */ }
            lastProjectFileNameRef.current = null;
        }
        setSelectedId(null);
        setArchitectFile(null);
        setSeedProject(null);
        setLatestProject(null);
        setEarthSessionKey(k => k + 1);
    }, []);

    const handleProjectChange = useCallback((project: SavedProject) => {
        lastProjectFileNameRef.current = project.fileName;
        setLatestProject(project);
        try {
            saveProject(project);
        } catch {
            /* quota — still keep in-memory / quote JSON */
        }
        onChange?.(project);
    }, [onChange]);

    const handleFiles = useCallback(async (incoming: File[]) => {
        if (!incoming.length) return;
        setUploading(true);
        setError('');
        const added: QuoteAttachment[] = [];
        try {
            for (const file of incoming) {
                const result = await uploadQuoteAttachment(file);
                added.push({
                    id: result.attachment_id,
                    name: defaultAttachmentName(result.filename),
                    filename: result.filename,
                    content_type: result.content_type,
                    uploaded_at: new Date().toISOString(),
                });
            }
            onFilesChange([...files, ...added]);
            const last = added[added.length - 1];
            if (last && isPdfAttachment(last)) {
                await openAttachment(last, null);
            }
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Upload failed');
        } finally {
            setUploading(false);
        }
    }, [files, onFilesChange, openAttachment]);

    const handleRemove = async (attachment: QuoteAttachment) => {
        setError('');
        try {
            if (isLocalAttachmentId(attachment.id)) {
                await deleteQuoteAttachment(attachment.id);
            }
            onFilesChange(files.filter(a => a.id !== attachment.id));
            if (selectedId === attachment.id) {
                setSelectedId(null);
                setArchitectFile(null);
                setSeedProject(null);
                setLatestProject(null);
            }
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Delete failed');
        }
    };

    const handleDownload = async (attachment: QuoteAttachment) => {
        setError('');
        try {
            await downloadQuoteAttachment(attachment);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Download failed');
        }
    };

    const handleBeforeSave = useCallback(async (blob: Blob, mimeType: string) => {
        const projectKey =
            (architectFile?.name ?? lastProjectFileNameRef.current) || null;
        if (projectKey) {
            const latest = loadSavedProject(projectKey);
            if (latest) {
                try { saveProject(latest); } catch { /* ignore */ }
                onChange?.(latest);
            }
        }

        const isPdf = mimeType === 'application/pdf';
        const isPng = mimeType === 'image/png';
        if (!isPdf && !isPng) return false;

        if (isPdf && !architectFile) return false;

        try {
            const filename = isPdf
                ? calculatedPdfFilename(architectFile!.name)
                : calculatedPngFilename(projectKey ?? 'earth');
            const file = new File([blob], filename, { type: mimeType });
            const result = await uploadQuoteAttachment(file);
            const entry: QuoteAttachment = {
                id: result.attachment_id,
                name: defaultAttachmentName(result.filename),
                filename: result.filename,
                content_type: result.content_type || mimeType,
                uploaded_at: new Date().toISOString(),
            };
            onFilesChange([
                ...files.filter(f => f.filename !== filename),
                entry,
            ]);
            notify(
                isPdf ? 'Calculated PDF saved to Views.' : 'Map image saved to Views.',
                { type: 'info' },
            );
            // Earth save: drop local draft and return to create-from-zero picker.
            if (isPng) {
                if (projectKey) {
                    try { removeSavedProject(projectKey); } catch { /* ignore */ }
                    lastProjectFileNameRef.current = null;
                }
                setSelectedId(null);
                setArchitectFile(null);
                setSeedProject(null);
                setLatestProject(null);
                setEarthSessionKey(k => k + 1);
            }
        } catch (e) {
            notify(
                e instanceof Error ? e.message : 'Failed to save calculated file',
                { type: 'error' },
            );
        }
        return false;
    }, [architectFile, files, onFilesChange, onChange, notify]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: PDF_ACCEPT,
        multiple: true,
        disabled: uploading,
        onDrop: dropped => void handleFiles(dropped),
    });

    return (
        <Paper variant="outlined" sx={{
            p: 3, width: '100%',
            fontFamily: 'Arial, sans-serif',
        }}>
            <Box
                sx={{
                    flexShrink: 0,
                    borderBottom: 4,
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                        Views
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                        Upload a PDF to annotate, or start from Google Earth below.
                    </Typography>
                    <Box
                        {...getRootProps()}
                        sx={{
                            py: 2,
                            px: 1.5,
                            textAlign: 'center',
                            border: '1px dashed',
                            borderColor: isDragActive ? 'primary.main' : 'divider',
                            borderRadius: 1,
                            bgcolor: isDragActive ? 'action.hover' : 'transparent',
                            cursor: uploading ? 'wait' : 'pointer',
                            opacity: uploading ? 0.7 : 1,
                        }}
                    >
                        <input {...getInputProps()} />
                        {uploading ? (
                            <CircularProgress size={22} />
                        ) : (
                            <>
                                <AttachFileIcon sx={{ color: 'text.disabled', fontSize: 22, mb: 0.5 }} />
                                <Typography variant="caption" color="text.secondary" display="block">
                                    {isDragActive ? 'Drop PDF here…' : 'Drop PDF or click to browse'}
                                </Typography>
                            </>
                        )}
                    </Box>
                    {architectFile && (
                        <Button
                            size="small"
                            fullWidth
                            startIcon={<PublicIcon />}
                            onClick={startFromZero}
                            sx={{ mt: 1 }}
                        >
                            Create from Google Earth
                        </Button>
                    )}
                    {error && (
                        <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                            {error}
                        </Typography>
                    )}
                </Box>

                <List dense sx={{ flex: 1, overflowY: 'auto', py: 0 }}>
                    {viewFiles.length === 0 && (
                        <Box sx={{ p: 2 }}>
                            <Typography variant="caption" color="text.disabled">
                                No view files yet
                            </Typography>
                        </Box>
                    )}
                    {viewFiles.map(attachment => {
                        const selected = selectedId === attachment.id;
                        const loading = loadingId === attachment.id;
                        const isPdf = isPdfAttachment(attachment);
                        const isImage = isImageViewAttachment(attachment);
                        return (
                            <ListItemButton
                                key={attachment.id}
                                selected={selected}
                                disabled={loading || uploading}
                                onClick={() => {
                                    if (isPdf) void openAttachment(attachment, initialProject);
                                    else if (isImage) setPreviewImage(attachment);
                                }}
                                sx={{ pr: 11 }}
                            >
                                <ListItemText
                                    primary={
                                        <Stack direction="row" spacing={0.75} alignItems="center">
                                            <Typography
                                                variant="body2"
                                                fontWeight={selected ? 700 : 500}
                                                noWrap
                                            >
                                                {attachment.name || attachment.filename}
                                            </Typography>
                                            {isPdf && (
                                                <Chip
                                                    size="small"
                                                    label="Measurable"
                                                    color="primary"
                                                    variant="outlined"
                                                    sx={{ height: 18, fontSize: 10, flexShrink: 0 }}
                                                />
                                            )}
                                        </Stack>
                                    }
                                    secondary={attachment.filename}
                                    primaryTypographyProps={{ component: 'div' }}
                                    secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                                />
                                {loading ? (
                                    <CircularProgress size={16} sx={{ mr: 1 }} />
                                ) : null}
                                <ListItemSecondaryAction>
                                    <IconButton
                                        edge="end"
                                        size="small"
                                        aria-label="Download file"
                                        onClick={e => {
                                            e.stopPropagation();
                                            void handleDownload(attachment);
                                        }}
                                    >
                                        <DownloadOutlinedIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        edge="end"
                                        size="small"
                                        color="error"
                                        aria-label="Remove file"
                                        onClick={e => {
                                            e.stopPropagation();
                                            void handleRemove(attachment);
                                        }}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItemButton>
                        );
                    })}
                </List>
            </Box>

            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    bgcolor: '#f5f6fa',
                }}
            >
                <Stack
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    sx={{ px: 0.5, py: 0.25, flexShrink: 0 }}
                >
                    <IconButton
                        size="small"
                        aria-label="Expand engineer software"
                        title="Expand"
                        onClick={() => setFullscreen(true)}
                        sx={{
                            '@media (hover: none) and (pointer: coarse)': {
                                minWidth: 44,
                                minHeight: 44,
                            },
                        }}
                    >
                        <OpenInFullIcon fontSize="small" />
                    </IconButton>
                </Stack>
                {!fullscreen ? (
                    architectFile ? (
                        <Box
                            key={`pdf-${selectedId ?? architectFile.name}`}
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                                backgroundColor: '#16171d',
                            }}
                        >
                            <Architect
                                file={architectFile}
                                fileName={architectFile.name}
                                sources={['pdf']}
                                initialProject={latestProject ?? seedProject ?? undefined}
                                checkSavedProjects={!latestProject && !seedProject}
                                onChange={handleProjectChange}
                                onBeforeSave={handleBeforeSave}
                                showSave
                                showChangeSource={false}
                                googleMapsApiKey={GOOGLE_MAPS_API_KEY}
                            />
                        </Box>
                    ) : (
                        <Box
                            key={`earth-${earthSessionKey}`}
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                                backgroundColor: '#16171d',
                            }}
                        >
                            <Architect
                                sources={['earth']}
                                initialProject={latestProject ?? undefined}
                                checkSavedProjects={false}
                                onChange={handleProjectChange}
                                onBeforeSave={handleBeforeSave}
                                showSave
                                showChangeSource={false}
                                googleMapsApiKey={GOOGLE_MAPS_API_KEY}
                            />
                        </Box>
                    )
                ) : (
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: '#e8eaf0',
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            Engineer software is expanded fullscreen
                        </Typography>
                    </Box>
                )}
            </Box>

            <Dialog
                open={fullscreen}
                onClose={() => setFullscreen(false)}
                fullScreen
                PaperProps={{
                    sx: {
                        m: 0,
                        maxWidth: '100%',
                        width: '100vw',
                        height: '100dvh',
                        bgcolor: '#16171d',
                    },
                }}
            >
                <IconButton
                    aria-label="Exit fullscreen"
                    title="Exit fullscreen"
                    onClick={() => setFullscreen(false)}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 2,
                        color: 'grey.300',
                        bgcolor: 'rgba(0,0,0,0.35)',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
                        '@media (hover: none) and (pointer: coarse)': {
                            minWidth: 44,
                            minHeight: 44,
                        },
                    }}
                >
                    <FullscreenExitIcon />
                </IconButton>
                <Box
                    sx={{
                        height: '100%',
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    {architectFile ? (
                        <Box
                            key={`fs-pdf-${selectedId ?? architectFile.name}`}
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                            }}
                        >
                            <Architect
                                file={architectFile}
                                fileName={architectFile.name}
                                sources={['pdf']}
                                initialProject={latestProject ?? seedProject ?? undefined}
                                checkSavedProjects={!latestProject && !seedProject}
                                onChange={handleProjectChange}
                                onBeforeSave={handleBeforeSave}
                                showSave
                                showChangeSource={false}
                                googleMapsApiKey={GOOGLE_MAPS_API_KEY}
                            />
                        </Box>
                    ) : (
                        <Box
                            key={`fs-earth-${earthSessionKey}`}
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                            }}
                        >
                            <Architect
                                sources={['earth']}
                                initialProject={latestProject ?? undefined}
                                checkSavedProjects={false}
                                onChange={handleProjectChange}
                                onBeforeSave={handleBeforeSave}
                                showSave
                                showChangeSource={false}
                                googleMapsApiKey={GOOGLE_MAPS_API_KEY}
                            />
                        </Box>
                    )}
                </Box>
            </Dialog>

            <QuoteAttachmentPreviewDialog
                attachment={previewImage}
                onClose={() => setPreviewImage(null)}
            />
        </Paper>
    );
}
